
// inspired by https://github.com/auth0/node-oauth2-jwt-bearer/blob/main/packages/access-token-jwt

import {fetchJson} from './fetch.ts';


export type DiscoverOptions = {
    fetch?: typeof fetch;
    URL?: typeof URL;
    Headers?: typeof Headers;
    issuerBaseUri: string;
    cacheMaxAge: number;
    timeout: number;
}

export interface IssuerMetadata {
    issuer: string;
    jwks_uri: string;
    id_token_signing_alg_values_supported?: string[];

    [key: string]: unknown;
}


const OIDC_DISCOVERY = '/.well-known/openid-configuration';
const OAUTH2_DISCOVERY = '/.well-known/oauth-authorization-server';

function checkIssuer(metadata: IssuerMetadata) {
    if (!metadata.issuer) {
        throw new Error(`'issuer' not found is authorization server metadata`);
    }
}

export async function discover(options: DiscoverOptions): Promise<IssuerMetadata> {
    const URLCtor: typeof URL = options.URL ?? (globalThis.URL ?? URL);
    const url = new URLCtor(options.issuerBaseUri);
    if (url.pathname.includes('/.well-known/')) {
        const metadata = await fetchJson<IssuerMetadata>(url, options.timeout, options);
        checkIssuer(metadata);
        return metadata;
    }

    const pathnameList: string[] = [];
    if (url.pathname.endsWith('/')) {
        pathnameList.push(`${url.pathname}${OIDC_DISCOVERY.substring(1)}`);
    } else {
        pathnameList.push(`${url.pathname}${OIDC_DISCOVERY}`);
    }
    if (url.pathname.endsWith('/')) {
        pathnameList.push(`${OAUTH2_DISCOVERY}`);
    } else {
        pathnameList.push(`${OAUTH2_DISCOVERY}${url.pathname}`);
    }

    for (const pathname of pathnameList) {
        try {
            const wellKnownUri = new URLCtor(pathname, url);
            const metadata = await fetchJson<IssuerMetadata>(wellKnownUri, options.timeout, options);
            checkIssuer(metadata);
            return metadata;
        }
        catch (err) {
            // noop
        }
    }

    throw new Error('Failed to fetch authorization server metadata');
}

export default (opts: DiscoverOptions): () => Promise<IssuerMetadata> => {
    let currentPromise: Promise<IssuerMetadata> | undefined;
    let timestamp = 0;
    return () => {
        const now = Date.now();
        if (!currentPromise || now > timestamp + opts.cacheMaxAge) {
            timestamp = now;
            currentPromise = discover(opts).catch((err) => {
                currentPromise = undefined;
                throw err;
            });
        }
        return currentPromise;
    }
}
